/**
 * Divisions on the website (`EX-DIV03`, `docs/29` §8, ADR-0125, Wave 29).
 *
 * A division is a band on the rating scale, half-open: `ratedFrom` inclusive
 * and `ratedBelow` exclusive, either `null` for no limit. Every answer about a
 * person (rated, unofficial, closed) is the server's `viewer.division`; this
 * module only words the band and names the rating bands it holds, so the tag
 * and its line read the same on the directory, the head, the board, the
 * result and the profile.
 */
import { formatNumber } from "@/lib/format-datetime";
import { ratingBand, type RatingBand } from "@/features/judge/lib/rating-band";
import type { ContestDivision } from "../types";

/** A rating as a division prints it: no grouping ("1600"), the reader's digits. */
export function formatRating(value: number, locale: string): string {
  return formatNumber(value, locale, { useGrouping: false });
}

/**
 * Which `contest.division` message words the band, with its values: `lineBelow`
 * for a band with only an upper edge (Div. 2 to 4), `lineFrom` for one with
 * only a lower edge (Div. 1), `lineRange` for both. A range prints its last
 * rated rating, one below the exclusive edge, so "1400 to 1599" never claims
 * 1600.
 */
export function divisionLineMessage(
  division: Pick<ContestDivision, "ratedFrom" | "ratedBelow">,
  locale: string,
): { key: "lineBelow" | "lineFrom" | "lineRange"; values: Record<string, string> } {
  const { ratedFrom, ratedBelow } = division;
  if (ratedFrom !== null && ratedBelow !== null) {
    return {
      key: "lineRange",
      values: { from: formatRating(ratedFrom, locale), to: formatRating(ratedBelow - 1, locale) },
    };
  }
  if (ratedFrom !== null) return { key: "lineFrom", values: { from: formatRating(ratedFrom, locale) } };
  return { key: "lineBelow", values: { below: formatRating(ratedBelow ?? 0, locale) } };
}

/**
 * The rating bands a division rates, lowest and highest (`null` highest: "and
 * up"), for decision 6's line "who a division is for" in our band names.
 */
export function divisionBands(division: Pick<ContestDivision, "ratedFrom" | "ratedBelow">): {
  low: RatingBand;
  high: RatingBand | null;
} {
  const low = division.ratedFrom === null ? "newcomer" : ratingBand(division.ratedFrom);
  const high = division.ratedBelow === null ? null : ratingBand(division.ratedBelow - 1);
  return { low, high };
}
